import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { gardienById } from "@/data/gardiens";

export const SITE_URL = "https://passerelle.site";

const ROBOTS_INDEX = "index, follow, max-image-preview:large, max-snippet:-1";

const upsert = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const applySeo = ({ title, description, path = "", image = "/og-image.jpg", robots = ROBOTS_INDEX }) => {
  document.title = title;
  upsert('meta[name="description"]', "meta", { name: "description", content: description });
  upsert('meta[name="robots"]', "meta", { name: "robots", content: robots });
  upsert('link[rel="canonical"]', "link", { rel: "canonical", href: `${SITE_URL}${path}` });
  upsert('meta[property="og:title"]', "meta", { property: "og:title", content: title });
  upsert('meta[property="og:description"]', "meta", { property: "og:description", content: description });
  upsert('meta[property="og:url"]', "meta", { property: "og:url", content: `${SITE_URL}${path}` });
  upsert('meta[property="og:image"]', "meta", { property: "og:image", content: `${SITE_URL}${image}` });
  upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title });
  upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: description });
  upsert('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: `${SITE_URL}${image}` });
};

const ROUTE_SEO = {
  "/": {
    title: "Passerelle — Des objets qui prennent soin de ceux qu'on aime",
    description: "Boutique d'objets personnalisés faits main et application de jeux doux pour accompagner les personnes atteintes d'Alzheimer, d'autisme ou de maladies dégénératives.",
  },
  "/boutique": {
    title: "Boutique — Passerelle",
    description: "Objets faits main à personnaliser : boîte à souvenirs, bougie parfumée, cœur en résine, plaid lesté… pensés pour les personnes fragilisées et leurs familles.",
  },
  "/gardiens": {
    title: "Les Gardiens des Souvenirs et des forces intérieures — Passerelle",
    description: "Dix-sept personnages bienveillants veillent sur les émotions précieuses et les forces intérieures. Rencontrez-les et trouvez celui de votre proche.",
  },
  "/quiz-gardien": {
    title: "Quiz — Trouver son Gardien — Passerelle",
    description: "Cinq questions tout en douceur pour découvrir le Gardien qui accompagnera le mieux votre proche, avec des activités et des objets adaptés.",
  },
  "/pathologies": {
    title: "Pathologies & Soins — Passerelle",
    description: "Comprendre Alzheimer, l'autisme et les maladies dégénératives : repères, conseils et approches douces pour les familles et les soignants.",
  },
  "/espace-jeux": {
    title: "Espace Jeux — Passerelle",
    description: "Jeux de mémoire, blind test musical, jeux sensoriels et exercices de concentration : des activités apaisantes pour stimuler en douceur.",
  },
  "/realisations": {
    title: "Réalisations — Passerelle",
    description: "Découvrez nos créations personnalisées : boîtes à souvenirs, bougies parfumées et cœurs en résine, fabriqués main pour raconter une histoire.",
  },
  "/personnalisation": {
    title: "Personnaliser un objet — Passerelle",
    description: "Racontez-nous son histoire : nous créons un objet unique, gravé de ses souvenirs, pour offrir de la douceur à la personne que vous accompagnez.",
  },
  "/temoignages": {
    title: "Témoignages — Passerelle",
    description: "Les mots des familles et des soignants qui utilisent Passerelle au quotidien : des moments de douceur, de lien et de souvenirs retrouvés.",
  },
  "/nos-techniques": {
    title: "Nos Techniques — Passerelle",
    description: "Gravure, bougies coulées main et inclusion en résine : découvrez les techniques artisanales derrière chaque objet Passerelle.",
  },
  "/forum": {
    title: "Forum des familles — Passerelle",
    description: "Un espace bienveillant pour échanger entre familles et soignants : questions, conseils et réconfort autour de l'accompagnement.",
  },
  "/contact": {
    title: "Contact — Passerelle",
    description: "Une question, une demande sur mesure, un souvenir à graver ? Écrivez-nous, nous répondons avec le cœur.",
  },
};

const NOINDEX_PATHS = ["/mon-compte", "/espace-soignant", "/paiement/succes", "/paiement/annule"];

// SEO par route, rendu une fois dans le Layout : met à jour les balises statiques en place (pas de doublons)
export const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/gardiens/")) {
      const g = gardienById(pathname.split("/")[2]);
      if (g) {
        applySeo({
          title: `${g.name} — ${g.title} — Passerelle`,
          description: `${g.value} — « ${g.quote} » Découvrez l'histoire de ${g.name}, ses activités douces et son objet fétiche.`,
          path: `/gardiens/${g.id}`,
          image: g.image,
        });
        return;
      }
    }
    const noindex = NOINDEX_PATHS.includes(pathname);
    const m = ROUTE_SEO[pathname] || ROUTE_SEO["/"];
    applySeo({
      ...m,
      path: pathname === "/" ? "" : pathname,
      robots: noindex ? "noindex, nofollow" : ROBOTS_INDEX,
    });
  }, [pathname]);
  return null;
};
